import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard/Dashboard';
import Budget from './pages/Budget';
import Income from './pages/Income';
import Protection from './pages/Protection';
import Assets from './pages/Assets';
import Liabilities from './pages/Liabilities';
import CashFlow from './pages/CashFlow';
import WealthPlan from './pages/WealthPlan';
import Sidebar from './components/Sidebar/Sidebar';
import FactFinder from './pages/FactFinder';
import FactFinderSummaryPage from './pages/FactFinderSummary';
import Spending from './pages/Spending';
import Accounts from './pages/Accounts';
import Transactions from './pages/Transactions';
import Onboarding from './pages/Onboarding';
import Welcome from './pages/Welcome';
import PlaidTest from './pages/PlaidTest';
import KnowledgeAdmin from './pages/KnowledgeAdmin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DynamicPrivacyPolicy from './pages/DynamicPrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Help from './pages/Help';
import DeveloperTools from './components/common/DeveloperTools';
import MobileHeader from './components/common/MobileHeader';
import MobileSidebar from './components/common/MobileSidebar';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import useIsMobile from './hooks/useIsMobile';
import './styles/App.css';

// Placeholder components - we'll create these next
const NotFound = () => <div>404 - Page Not Found</div>;

function AppLayout() {
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleMenuPress = () => {
    setIsMobileSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  if (isMobile) {
    // Mobile Layout - column direction
    return (
      <View style={styles.mobileContainer}>
        <MobileHeader onMenuPress={handleMenuPress} />
        <View style={styles.mobileContent}>
          <View style={styles.mobilePageContent}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/income" element={<Income />} />
              <Route path="/budget" element={<Budget />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/protection" element={<Protection />} />
              <Route path="/assets" element={<Assets />} />
              <Route path="/liabilities" element={<Liabilities />} />
              <Route path="/cashflow" element={<CashFlow />} />
              <Route path="/wealth-plan" element={<WealthPlan />} />
              <Route path="/fact-finder" element={<FactFinder />} />
              <Route path="/fact-finder-summary" element={<FactFinderSummaryPage />} />
              <Route path="/spending" element={<Spending />} />
              <Route path="/plaid-test" element={<PlaidTest />} />
              <Route path="/knowledge-admin" element={<KnowledgeAdmin />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy-dynamic" element={<DynamicPrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/help" element={<Help />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </View>
          <Footer />
        </View>
        <MobileSidebar 
          isOpen={isMobileSidebarOpen} 
          onClose={handleSidebarClose} 
        />
      </View>
    );
  }

  // Desktop Layout - column direction with full-width footer
  return (
    <View style={styles.desktopContainer}>
      <Header />
      <View style={styles.mainContentArea}>
        <Sidebar />
        <View style={styles.content}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/income" element={<Income />} />
            <Route path="/budget" element={<Budget />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/protection" element={<Protection />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/liabilities" element={<Liabilities />} />
            <Route path="/cashflow" element={<CashFlow />} />
            <Route path="/wealth-plan" element={<WealthPlan />} />
            <Route path="/fact-finder" element={<FactFinder />} />
            <Route path="/fact-finder-summary" element={<FactFinderSummaryPage />} />
            <Route path="/spending" element={<Spending />} />
            <Route path="/plaid-test" element={<PlaidTest />} />
            <Route path="/knowledge-admin" element={<KnowledgeAdmin />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy-dynamic" element={<DynamicPrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/help" element={<Help />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </View>
      </View>
      <Footer />
    </View>
  );
}

// Component to handle onboarding check and routing
function OnboardingWrapper() {
  // Read initial state directly from localStorage
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(
    () => localStorage.getItem('zecher_onboarding_completed') === 'true'
  );

  const handleOnboardingComplete = () => {
    setHasCompletedOnboarding(true);
  };

  return (
    <Routes>
      {/* If onboarding not completed, show welcome and onboarding */}
      {!hasCompletedOnboarding ? (
        <>
          <Route path="/welcome" element={<Welcome />} />
          <Route
            path="/onboarding"
            element={<Onboarding onComplete={handleOnboardingComplete} />}
          />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </>
      ) : (
        /* If onboarding completed, show main app */
        <>
          <Route path="/onboarding" element={<Navigate to="/" replace />} />
          <Route path="/*" element={<AppLayout />} />
        </>
      )}
    </Routes>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row', // Default for desktop
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
  },
  mobileContainer: {
    flex: 1,
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
  },
  desktopContainer: {
    flex: 1,
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
  },
  mainContentArea: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0, // Allow flex shrinking
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  mobileContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    paddingTop: 0,
  },
  mobilePageContent: {
    flex: 1,
    overflow: 'auto',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
  },
});

function App() {
  return (
    <Router>
      <OnboardingWrapper />
      <DeveloperTools />
    </Router>
  );
}

export default App; 